import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Activity, Mail, Lock, Eye, EyeOff, AlertCircle, Shield } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      
      // Check if 2FA is required
      if (result?.requires_2fa) {
        setRequires2FA(true);
        setTempToken(result.temp_token);
        toast.info('Enter your authenticator code');
        setLoading(false);
        return;
      }
      
      toast.success('Welcome back!');
      navigate('/board');
    } catch (err) {
      setError(err.message || 'Login failed');
      toast.error(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/verify-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_token: tempToken, code: totpCode }),
        credentials: 'include'
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || '2FA verification failed');
      }

      // Store token and user in auth context
      localStorage.setItem('token', data.token);
      window.location.href = '/board';  // Full reload to update auth state
    } catch (err) {
      setError(err.message || '2FA verification failed');
      toast.error(err.message || '2FA verification failed');
    } finally {
      setLoading(false);
    }
  };

  // 2FA Input Screen
  if (requires2FA) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
              <Activity className="w-7 h-7 text-white" />
            </div>
            <span className="font-display text-2xl font-bold tracking-tight uppercase">
              SportsAnalysisAB
            </span>
          </Link>

          {/* Card */}
          <div className="bg-card border border-border rounded-sm p-8">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8 text-blue-400" />
              </div>
            </div>
            <h1 className="font-display text-2xl font-bold uppercase tracking-tight text-center mb-2">
              Two-Factor Authentication
            </h1>
            <p className="text-sm text-muted-foreground text-center mb-8">
              Enter the 6-digit code from your authenticator app
            </p>

            {/* Error message */}
            {error && (
              <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-sm p-3 mb-6">
                <AlertCircle className="w-4 h-4 text-red-400" />
                <span className="text-sm text-red-400">{error}</span>
              </div>
            )}

            {/* 2FA Form */}
            <form onSubmit={handle2FASubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="totp">Authenticator Code</Label>
                <Input
                  id="totp"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-2xl tracking-[0.5em] font-mono rounded-sm"
                  required
                  autoFocus
                  data-testid="totp-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full btn-blaze rounded-sm font-bold uppercase tracking-wider"
                disabled={loading || totpCode.length !== 6}
                data-testid="verify-2fa-btn"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </Button>
            </form>

            <Button 
              variant="ghost"
              className="w-full mt-4 text-muted-foreground"
              onClick={() => {
                setRequires2FA(false);
                setTotpCode('');
                setTempToken('');
                setError('');
              }}
            >
              Back to Login
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center mt-6">
            Lost access to your authenticator? Contact support.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
            <Activity className="w-7 h-7 text-white" />
          </div>
          <span className="font-display text-2xl font-bold tracking-tight uppercase">
            SportsAnalysisAB
          </span>
        </Link>

        {/* Card */}
        <div className="bg-card border border-border rounded-sm p-8">
          <h1 className="font-display text-2xl font-bold uppercase tracking-tight text-center mb-2">
            Welcome Back
          </h1>
          <p className="text-sm text-muted-foreground text-center mb-8">
            Sign in to access your analytics dashboard
          </p>

          {/* Error message */}
          {error && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-sm p-3 mb-6">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <span className="text-sm text-red-400">{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 rounded-sm"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 rounded-sm"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-blaze rounded-sm font-bold uppercase tracking-wider"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Sign up link */}
          <p className="text-sm text-muted-foreground text-center mt-6">
            Don't have an account?{' '}
            <Link to="/signup" className="text-foreground hover:underline font-medium">
              Sign up
            </Link>
          </p>
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-muted-foreground text-center mt-6">
          For entertainment purposes only. 18+ required.
        </p>
      </div>
    </div>
  );
}
